/**
 * ImageLightbox — Fullscreen image viewer with pinch-zoom (mobile)
 * and keyboard navigation. Pure React, no external libraries.
 *
 * Props:
 *   images        — array of { src, alt }
 *   activeIndex   — number
 *   onClose       — function
 *   onIndexChange — function(newIndex)
 */
import { useEffect, useRef, useCallback, useState } from 'react';

export default function ImageLightbox({ images, activeIndex, onClose, onIndexChange }) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Pinch-zoom state
  const [scale, setScale] = useState(1);
  const initialDistanceRef = useRef(null);
  const initialScaleRef = useRef(1);

  const safeImages = Array.isArray(images) && images.length > 0 ? images : [];
  const hasMultiple = safeImages.length > 1;
  const currentImage = safeImages[activeIndex] || null;

  // --- Focus trap: capture + restore focus ---
  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  // --- Keyboard navigation ---
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight' && hasMultiple) {
        e.preventDefault();
        onIndexChange((activeIndex + 1) % safeImages.length);
      } else if (e.key === 'ArrowLeft' && hasMultiple) {
        e.preventDefault();
        onIndexChange((activeIndex - 1 + safeImages.length) % safeImages.length);
      }
    },
    [onClose, onIndexChange, activeIndex, safeImages.length, hasMultiple],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Lock body scroll while lightbox is open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Reset scale when image changes
  useEffect(() => {
    setScale(1);
  }, [activeIndex]);

  // --- Pinch-zoom touch handlers ---
  const getFingerDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2) {
        initialDistanceRef.current = getFingerDistance(e.touches);
        initialScaleRef.current = scale;
      }
    },
    [scale],
  );

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && initialDistanceRef.current !== null) {
      const currentDistance = getFingerDistance(e.touches);
      const ratio = currentDistance / initialDistanceRef.current;
      const newScale = Math.max(0.5, Math.min(5, initialScaleRef.current * ratio));
      setScale(newScale);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    initialDistanceRef.current = null;
    setScale((prev) => (prev < 1.1 ? 1 : prev));
  }, []);

  // --- Backdrop click ---
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  if (!currentImage) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
      tabIndex={-1}
      className="fixed inset-0 z-50 bg-carbon-black/90 flex items-center justify-center outline-none"
      onClick={handleBackdropClick}
    >
      {/* Close button */}
      <button
        type="button"
        className="absolute top-4 right-4 text-white text-3xl cursor-pointer hover:text-n3 z-10 w-12 h-12 flex items-center justify-center"
        onClick={onClose}
        aria-label="Close lightbox"
      >
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Left arrow */}
      {hasMultiple && (
        <button
          type="button"
          className="absolute top-1/2 -translate-y-1/2 left-4 bg-white/20 hover:bg-white/40 rounded-full w-12 h-12 flex items-center justify-center text-white z-10"
          onClick={() => onIndexChange((activeIndex - 1 + safeImages.length) % safeImages.length)}
          aria-label="Previous image"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
      )}

      {/* Right arrow */}
      {hasMultiple && (
        <button
          type="button"
          className="absolute top-1/2 -translate-y-1/2 right-4 bg-white/20 hover:bg-white/40 rounded-full w-12 h-12 flex items-center justify-center text-white z-10"
          onClick={() => onIndexChange((activeIndex + 1) % safeImages.length)}
          aria-label="Next image"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}

      {/* Image with pinch-zoom */}
      <div
        className="flex items-center justify-center"
        style={{ touchAction: 'pinch-zoom' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentImage.src}
          alt={currentImage.alt || `Image ${activeIndex + 1}`}
          className="max-w-[90vw] max-h-[85vh] object-contain select-none"
          style={{
            transform: `scale(${scale})`,
            transition: 'transform 0.1s ease-out',
          }}
          draggable={false}
        />
      </div>

      {/* Image counter */}
      {hasMultiple && (
        <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm font-sandvik-text">
          {activeIndex + 1} / {safeImages.length}
        </p>
      )}
    </div>
  );
}
